import { getCanonicalRenderedVerdictAsset } from './verdictAssets';

function hasDisplayableVerdictPayload(payload) {
  if (!payload || typeof payload !== 'object') return false;
  const winnerIdx = Number(payload.winning_option_index);
  const totalVotes = Number(payload.total_votes);
  const tallies = payload.tallies;
  return Number.isFinite(winnerIdx)
    && Number.isFinite(totalVotes)
    && tallies
    && typeof tallies === 'object';
}

function normalizeTallies(tallies, optionCount = 0) {
  const out = {};
  if (tallies && typeof tallies === 'object') {
    Object.entries(tallies).forEach(([key, value]) => {
      const idx = Number(key);
      const count = Number(value);
      if (!Number.isFinite(idx) || idx < 0) return;
      out[idx] = Math.max(0, Number.isFinite(count) ? count : 0);
    });
  }
  for (let i = 0; i < optionCount; i += 1) {
    if (!Number.isFinite(Number(out[i]))) out[i] = 0;
  }
  return out;
}

function buildDerivedVerdictText(resultRow, decisionRow) {
  const payload = hasDisplayableVerdictPayload(resultRow?.final_verdict)
    ? resultRow.final_verdict
    : (hasDisplayableVerdictPayload(resultRow) ? resultRow : null);
  if (payload?.verdict_text) return payload.verdict_text;

  const options = Array.isArray(decisionRow?.options)
    ? decisionRow.options
    : [decisionRow?.option_a, decisionRow?.option_b].filter(Boolean);
  const tallies = normalizeTallies(payload?.tallies || resultRow?.tallies, options.length);
  const totalVotes = Number(
    payload?.total_votes ??
    resultRow?.total_votes ??
    Object.values(tallies).reduce((a, b) => a + Number(b || 0), 0)
  );
  const explicitWinner = Number(payload?.winning_option_index ?? resultRow?.winning_option_index);
  const ranked = Object.entries(tallies)
    .map(([idx, count]) => ({ idx: Number(idx), count: Number(count || 0) }))
    .filter((entry) => Number.isFinite(entry.idx))
    .sort((a, b) => (b.count - a.count) || (a.idx - b.idx));
  const winnerIdx = Number.isFinite(explicitWinner) ? explicitWinner : (ranked[0]?.idx ?? 0);
  if (!Number.isFinite(winnerIdx)) return null;
  const winnerLabel = options[winnerIdx] || `Option ${winnerIdx + 1}`;
  if (totalVotes === 0) return `No votes were cast. Defaulting to ${winnerLabel}.`;
  return `${winnerLabel} won with ${totalVotes} vote${totalVotes === 1 ? '' : 's'}.`;
}

export function resolveDailyVerdictRenderState(resultRow, decisionRow) {
  const memeStatus = String(resultRow?.meme_status || '').toLowerCase();
  const decisionStatus = String(decisionRow?.status || '').toLowerCase();
  const canonicalAsset = getCanonicalRenderedVerdictAsset({
    ...(resultRow || {}),
    ...(decisionRow || {}),
    id: decisionRow?.id || resultRow?.decision_id || resultRow?.id || null,
    decision_id: resultRow?.decision_id || decisionRow?.id || null,
    final_verdict: resultRow?.final_verdict || null,
  });
  const imageUrl = canonicalAsset.url;
  const text = resultRow?.meme_snark_text || buildDerivedVerdictText(resultRow, decisionRow) || null;
  const hasUsableImage = typeof imageUrl === 'string' && imageUrl.trim().length > 0;
  const hasRenderable = Boolean(hasUsableImage || text);
  const generatedAt =
    resultRow?.meme_generated_at ||
    resultRow?.verdict_generated_at ||
    resultRow?.last_updated_at ||
    resultRow?.created_at ||
    null;
  const imageUrlLower = String(imageUrl || '').toLowerCase();
  const hasDeterministicFallbackImage = Boolean(
    imageUrlLower &&
    (
      imageUrlLower.includes('/safe-fallback.svg') ||
      imageUrlLower.includes('safe-fallback.svg') ||
      imageUrlLower.includes('deterministic')
    )
  );
  const isCompleteStatus = ['ready', 'completed'].includes(memeStatus);
  const isTerminalDecisionStatus = ['closed', 'complete', 'completed', 'verdict_ready', 'revealed', 'archived'].includes(decisionStatus);
  const hasAuthoritativeVerdict =
    hasDisplayableVerdictPayload(resultRow?.final_verdict) ||
    hasDisplayableVerdictPayload(resultRow) ||
    Boolean(text);

  const isReady =
    hasUsableImage ||
    (hasDeterministicFallbackImage && Boolean(generatedAt)) ||
    (isCompleteStatus && Boolean(text)) ||
    (isTerminalDecisionStatus && hasAuthoritativeVerdict);
  const isFailed = !isReady && (memeStatus === 'failed' || memeStatus === 'permanently_failed');
  const isProcessing = !isReady && !isFailed;

  let renderState = 'processing';
  if (isReady) renderState = 'ready';
  else if (isFailed) renderState = 'failed';

  return {
    renderState,
    memeStatus,
    imageUrl: isReady ? imageUrl : null,
    text: isReady ? text : null,
    hasRenderable,
    verdictReady: isReady,
    verdictFailed: isFailed,
    verdictProcessing: isProcessing,
    canonicalVerdictAt: generatedAt,
  };
}
